import { EventBus, EVENTS } from '../../../../shared/EventBus';
import { progressionRefusal } from './talentPresentation';

export type TalentRequest = { kind: 'spend'; treeName: string; nodeId: string } | { kind: 'respec' };
export interface TalentRequestSnapshot { pending: TalentRequest | null; feedback: { message: string; refused: boolean } | null }
export interface TalentRoom {
  sessionId?: string; roomId?: string;
  onMessage(type: string, handler: (payload: any) => void): (() => void) | void;
}
interface Loan { owner: string; room: TalentRoom; key: string; sessionId: string; epoch: number; request: TalentRequest; dispose: () => void }
interface ConnectedIdentity { roomId: string; roomInstanceId: string | null; sessionId: string; clientInstanceId: string }
interface NetworkIdentityReader {
  getRoom?: () => TalentRoom | null;
  getRoomData?: () => { roomId: string; roomInstanceId?: string; sessionId: string } | null;
  getClientInstanceId?: () => string;
}
const manager = (): NetworkIdentityReader | undefined => (globalThis as { nm?: NetworkIdentityReader }).nm;
const currentRoom = (): TalentRoom | null => manager()?.getRoom?.() ?? null;
const identityKey = (identity: ConnectedIdentity): string => JSON.stringify([
  identity.roomId, identity.roomInstanceId, identity.sessionId, identity.clientInstanceId,
]);
/** The SDK room/schema do not carry server/client instance IDs; read real manager getters. */
function fallbackKey(room: TalentRoom | null): string {
  if (!room?.sessionId) return '';
  const data = manager()?.getRoomData?.();
  const sameSession = data?.sessionId === room.sessionId;
  return identityKey({ roomId: sameSession ? data!.roomId : room.roomId ?? 'unknown',
    roomInstanceId: sameSession ? data!.roomInstanceId ?? null : null,
    sessionId: room.sessionId, clientInstanceId: manager()?.getClientInstanceId?.() ?? '' });
}
const EMPTY: TalentRequestSnapshot = { pending: null, feedback: null };

/** Retained receiver survives window closure; transport retirement never resends a request. */
export class TalentLoanStore {
  private owner: string | null = null;
  private room: TalentRoom | null = null;
  private key = '';
  private epoch = 0;
  private connected: { room: TalentRoom; sessionId: string; key: string } | null = null;
  private blockedRoom: TalentRoom | null = null;
  private bindingNetwork = false;
  private loan: Loan | null = null;
  private snapshot: TalentRequestSnapshot = EMPTY;
  private observers = new Set<() => void>();
  private networkDisposers: Array<() => void> = [];
  constructor(private readonly getRoom: () => TalentRoom | null = currentRoom) {}

  getSnapshot = (): TalentRequestSnapshot => this.snapshot;
  private publish(snapshot: TalentRequestSnapshot): void {
    this.snapshot = snapshot;
    for (const observer of this.observers) observer();
    this.updateNetworkLifetime();
  }
  private retire(message: string | null): void {
    const pending = Boolean(this.loan);
    this.epoch += 1;
    this.loan?.dispose(); this.loan = null;
    this.publish({ pending: null, feedback: pending && message ? { message, refused: true } : null });
  }
  sync(owner: string | null): void {
    const observed = this.getRoom();
    const room = observed === this.blockedRoom ? null : observed;
    const key = this.connected?.room === room && this.connected.sessionId === room?.sessionId
      ? this.connected.key : fallbackKey(room);
    if (this.owner === owner && this.room === room && this.key === key) return;
    const sameOwner = this.owner === owner;
    this.epoch += 1;
    const wasPending = Boolean(this.loan);
    this.loan?.dispose(); this.loan = null;
    this.owner = owner; this.room = room; this.key = key;
    this.publish({ pending: null, feedback: wasPending && sameOwner ? { refused: true,
      message: 'The connection changed before the result arrived. Check your saved talents before trying again.' } : null });
  }
  subscribe = (observer: () => void): (() => void) => {
    this.observers.add(observer); this.updateNetworkLifetime();
    return () => { this.observers.delete(observer); this.updateNetworkLifetime(); };
  };
  private updateNetworkLifetime(): void {
    if ((this.loan || this.observers.size) && this.networkDisposers.length === 0 && !this.bindingNetwork) {
      const bus = EventBus.getInstance();
      this.bindingNetwork = true;
      this.networkDisposers = [
        bus.on(EVENTS.NET_DISCONNECTED, () => {
          this.blockedRoom = this.room; this.connected = null;
          this.room = null; this.key = '';
          this.retire('The connection ended before the result arrived. Check your saved talents after reconnecting.');
        }),
        bus.on(EVENTS.NET_CONNECTED, data => {
          const room = this.getRoom();
          // Reject an old transport's queued connection event as well as old replies.
          if (!room || data.sessionId !== room.sessionId || (room.roomId && room.roomId !== data.roomId)) return;
          this.connected = { room, sessionId: data.sessionId, key: identityKey(data) };
          this.blockedRoom = null;
          this.sync(this.owner);
        }),
      ];
      this.bindingNetwork = false;
      this.updateNetworkLifetime();
    } else if (!this.loan && this.observers.size === 0) {
      for (const dispose of this.networkDisposers) dispose();
      this.networkDisposers = [];
    }
  }
  begin(owner: string | null, request: TalentRequest, dispatch: () => void): boolean {
    this.sync(owner);
    if (!owner || !this.room?.sessionId || !this.key || this.loan) return false;
    const loan: Loan = { owner, room: this.room, key: this.key, sessionId: this.room.sessionId, epoch: this.epoch, request, dispose: () => {} };
    this.loan = loan;
    const receive = (payload: any) => {
      if (this.loan !== loan || this.owner !== loan.owner || this.getRoom() !== loan.room
        || this.epoch !== loan.epoch || this.key !== loan.key || loan.room.sessionId !== loan.sessionId || this.blockedRoom === loan.room || !payload || typeof payload.ok !== 'boolean') return;
      if (loan.request.kind === 'spend' && (payload.nodeId !== loan.request.nodeId || payload.treeName !== loan.request.treeName)) return;
      if (loan.request.kind === 'respec' && payload.quote && payload.quote.action !== 'talent_respec') return;
      // Dispose before notifying observers, which may immediately begin another request.
      loan.dispose(); this.loan = null;
      this.publish({ pending: null, feedback: { message: payload.ok ? 'Your talents have been updated.' : progressionRefusal(payload.reason), refused: !payload.ok } });
    };
    const dispose = loan.room.onMessage(request.kind === 'spend' ? 'spendTalentResult' : 'respecTalentsResult', receive);
    if (typeof dispose === 'function') loan.dispose = dispose;
    this.publish({ pending: request, feedback: null });
    try { dispatch(); return true; } catch {
      if (this.loan === loan) { loan.dispose(); this.loan = null; this.publish({ pending: null, feedback: { message: 'This change could not be sent. Please try again.', refused: true } }); }
      return false;
    }
  }
  /** Tests only: releases retained fixture receivers and their event listeners. */
  reset(): void {
    this.loan?.dispose(); this.loan = null;
    for (const dispose of this.networkDisposers) dispose();
    this.networkDisposers = []; this.owner = null; this.room = null; this.key = ''; this.epoch += 1; this.connected = null; this.blockedRoom = null; this.snapshot = EMPTY; this.observers.clear();
  }
}

export const talentLoanStore = new TalentLoanStore();
